import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MarkdownContent from './MarkdownContent';
import { useReport } from '../contexts/ReportContext';

const SpecContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
`;

const SubTabNav = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    transition: background ${({ theme }) => theme.transitions.base};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textMuted};
  }
`;

const SubTabButton = styled.button<{ active: boolean }>`
  flex: 0 0 auto;
  min-width: fit-content;
  padding: 0.75rem 1rem;
  border: none;
  background: ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active, theme }) => active ? 'white' : theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md} 0 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all ${({ theme }) => theme.transitions.base};
  margin-right: 0.5rem;
  white-space: nowrap;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primaryHover : theme.colors.surfaceSecondary};
  }
`;

const SubTabContent = styled.div`
  min-height: 300px;
`;

const QAItem = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const QAQuestion = styled.div`
  margin-bottom: 1.5rem;
`;

const QAQuestionTitle = styled.h6`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  margin-bottom: 0.5rem;
  font-size: 1rem;
`;

const QAQuestionContent = styled.div`
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.primaryLight};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 0 ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0;
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const QAResult = styled.div``;

const QAResultTitle = styled.h6`
  color: ${({ theme }) => theme.colors.success};
  font-weight: bold;
  margin-bottom: 0.5rem;
  font-size: 1rem;
`;

const QAResultContent = styled.div`
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  overflow-x: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyStateDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 300px;
  margin: 0;
`;

const SpecItem = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const SpecQuestion = styled.div`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  padding-left: 1rem;
  font-weight: 600;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primaryLight}, transparent);
  border-radius: 0 ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const SpecResult = styled.div`
  padding: 1rem 0 0 1rem;
  border-left: 2px dashed ${({ theme }) => theme.colors.border};
  margin-left: 1rem;
  
  p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
`;

const SpecificationTab: React.FC = () => {
  const { reportState, isAutoTriggeringFromUrl, hasChatStarted } = useReport();
  const [activeSubTab, setActiveSubTab] = useState<number>(0);

  // 仕切り直し・中止で内容が空に戻ったら、選んでいた「Q N」も先頭に戻す
  // 本文つきの「新しいインシデント」では、空を経ずに前より少ない件数で置き換わるので、件数が減ったときも戻す
  const specCount = reportState.specificationAnalysis.length;
  const prevSpecCountRef = useRef(specCount);
  useEffect(() => {
    if (specCount === 0 || specCount < prevSpecCountRef.current) setActiveSubTab(0);
    prevSpecCountRef.current = specCount;
  }, [specCount]);
  
  const hasSpecData = reportState.specificationAnalysis.length > 0;
  const shouldShowSpec = hasSpecData && (hasChatStarted || isAutoTriggeringFromUrl);

  if (!shouldShowSpec) {
    return (
      <SpecContainer>
        <EmptyState>
          <EmptyStateIcon>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </EmptyStateIcon>
          <EmptyStateTitle>仕様確認結果</EmptyStateTitle>
          <EmptyStateDescription>
            チャットでインシデントについて質問すると、ここに仕様確認結果が表示されます。
          </EmptyStateDescription>
        </EmptyState>
      </SpecContainer>
    );
  }

  const truncateQuestion = (question: string, maxLength: number = 30) => {
    if (question.length <= maxLength) return question;
    return question.substring(0, maxLength) + '...';
  };

  const renderActiveQA = () => {
    const currentQA = reportState.specificationAnalysis[activeSubTab];
    if (!currentQA) return null;

    return (
      <QAItem>
        <QAQuestion>
          <QAQuestionTitle>質問</QAQuestionTitle>
          <QAQuestionContent>
            {currentQA.question}
          </QAQuestionContent>
        </QAQuestion>
        <QAResult>
          <QAResultTitle>結果</QAResultTitle>
          <QAResultContent>
            <MarkdownContent>{currentQA.result}</MarkdownContent>
          </QAResultContent>
        </QAResult>
      </QAItem>
    );
  };

  return (
    <SpecContainer>
      <SubTabNav>
        {reportState.specificationAnalysis.map((spec, index) => (
          <SubTabButton
            key={index}
            active={activeSubTab === index}
            onClick={() => setActiveSubTab(index)}
            title={spec.question}
          >
            Q{index + 1}: {truncateQuestion(spec.question)}
          </SubTabButton>
        ))}
      </SubTabNav>
      
      <SubTabContent>
        {renderActiveQA()}
      </SubTabContent>
    </SpecContainer>
  );
};

export default SpecificationTab;